import Button from '@ixigo/iui/Button';
import Input from '@ixigo/iui/Input';
import Typography from '@ixigo/iui/Typography';
import { ChangeEvent, FC, useCallback, useState } from 'react';
import ExclamationCircle from '@ixigo/icons/ExclamationCircle';
import { useConversation } from '../../contexts/conversation';
import { ActorType, InputType, WidgetName } from '../../enums/conversation';
import CheckboxList from '../CheckboxList';
import Dialog from '../Dialog';
import DialogHeader from '../DialogHeader';
import FileUploader from './FileUploader';
import MessageBubble from '../MessageBubble';
import RadioList from '../RadioList';
import { ListItem } from '../RadioList/index.types';
import Textarea from '../Textarea';
import FormReview from './FormReview';
import {
    FormValues,
    InputFileConfig,
    InputFormConfig,
    InputSelectConfig,
    InputTextConfig,
    InputTextareaConfig,
} from './index.types';
import { uploadFile } from '../../apis/common';
import { getFileType } from '../../utils/conversation';
import Toast from '../Toast';

const TOAST_MESSAGE = 'Cannot upload files larger than the allowed size';

const FormInput: FC = () => {
    const { input, sendMessage } = useConversation();
    const [value, setValue] = useState<FormValues>([]);
    const {
        elements,
        submitBtnLabel,
        buttonList,
        required,
        useDialog,
        dialogTitle,
        review,
        reviewDialogTitle,
        reviewDialogDescription,
        reviewBtnLabel,
    } = input!.config as InputFormConfig;
    const [dialogOpen, setDialogOpen] = useState(false);
    const [reviewOpen, setReviewOpen] = useState(false);
    const [toastVisible, setToastVisible] = useState(false);
    const [loading, setLoading] = useState(false);

    // Helper to normalize value for FileUploader
    const getFileUploaderValue = (val: FormValues[0], multiple?: boolean): File | File[] | undefined => {
        if (multiple) {
            if (Array.isArray(val)) {
                return val.filter((f): f is File => f instanceof File);
            }
            return val instanceof File ? [val] : [];
        }
        return val instanceof File ? val : undefined;
    };

    const submitForm = async () => {
        setLoading(true);
        try {
            const messageParts = [];
            const inputValue: Record<string, FormValues[0]> = {};
            // Collect all file upload promises and their indices
            const fileUploadTasks: { index: number; files: File[] }[] = [];
            for (let index = 0; index < elements.length; index += 1) {
                const { type } = elements[index];
                const val = value[index];
                if (type === InputType.FILE && val) {
                    const files = Array.isArray(val)
                        ? val.filter((f): f is File => f instanceof File)
                        : [val].filter((f): f is File => f instanceof File);
                    if (files.length > 0) {
                        fileUploadTasks.push({ index, files });
                    }
                }
            }
            // Upload all files in parallel
            const uploadResults: Record<number, any> = {};
            await Promise.all(
                fileUploadTasks.map(async ({ index, files }) => {
                    const uploadedFiles = await Promise.all(
                        files.map(async (file) => {
                            const formData = new FormData();
                            formData.append('file', file);
                            const res = await uploadFile(formData);
                            return {
                                name: file.name,
                                size: file.size,
                                type: getFileType(file.type),
                                url: res.secure_url,
                            };
                        }),
                    );
                    uploadResults[index] = Array.isArray(value[index]) ? uploadedFiles : uploadedFiles[0];
                }),
            );
            // Now build messageParts and inputValue
            for (let index = 0; index < elements.length; index += 1) {
                const { label, type, id } = elements[index];
                const val = value[index];
                let displayValue = '';
                let partValue: any = val;
                if (type === InputType.FILE && uploadResults[index]) {
                    partValue = uploadResults[index];
                    displayValue = Array.isArray(partValue) ? partValue.map((f) => f.name).join(', ') : partValue.name;
                } else if (
                    Array.isArray(val) &&
                    val.every((item): item is ListItem => 'id' in item && 'label' in item && 'value' in item)
                ) {
                    displayValue = val.map((item) => item.label).join(', ');
                } else if (typeof val === 'string') {
                    displayValue = val.trim();
                } else if (typeof val === 'object' && val !== null && 'id' in val && 'label' in val && 'value' in val) {
                    displayValue = (val as ListItem).label;
                }
                inputValue[id] = partValue;
                if (
                    val !== undefined &&
                    val !== null &&
                    !(typeof val === 'string' && val.trim() === '') &&
                    !(Array.isArray(val) && val.length === 0)
                ) {
                    if (type === InputType.FILE && partValue) {
                        // Single or multiple files
                        if (Array.isArray(partValue)) {
                            partValue.forEach((fileObj) => {
                                messageParts.push({
                                    isPrivate: false,
                                    file: fileObj,
                                });
                            });
                        } else {
                            messageParts.push({
                                isPrivate: false,
                                file: partValue,
                            });
                        }
                    } else {
                        messageParts.push({
                            isPrivate: false,
                            widget: {
                                name: WidgetName.HTML,
                                parameters: {
                                    content: `<p class="body-xs text-charcoal-600">${label}</p><p>${displayValue}</p>`,
                                },
                            },
                        });
                    }
                }
            }
            sendMessage({ messageParts, input: inputValue });
            if (useDialog) setDialogOpen(false);
            if (review) setReviewOpen(false);
        } finally {
            setLoading(false);
        }
    };

    const onButtonClick = (button: NonNullable<InputFormConfig['buttonList']>[0]) => {
        const messageParts = [
            {
                isPrivate: false,
                text: {
                    content: button.name,
                },
            },
        ];

        sendMessage({ messageParts, input: button });
    };

    const handleSubmit = async () => {
        if (review) {
            setReviewOpen(true);
            return;
        }
        await submitForm();
    };

    const onChange = (val: FormValues[0], index: number) => {
        const { type, config } = elements[index];
        if (type === InputType.FILE) {
            const fileConfig = config as InputFileConfig;
            if (fileConfig.multiple) {
                setValue((prev) => {
                    const newValue = [...prev];
                    if (Array.isArray(val)) {
                        newValue[index] = val.filter((f): f is File => f instanceof File);
                    } else if (val instanceof File) {
                        newValue[index] = [val];
                    } else {
                        newValue[index] = [];
                    }
                    return newValue;
                });
                return;
            }
        }
        setValue((prev) => {
            const newValue = [...prev];
            newValue[index] = val;
            return newValue;
        });
    };

    const getIsDisabled = () => {
        if (!required) return false;

        if (required === 'every') {
            if (value.length === 0) return true;
            let isEveryFieldFilled = true;
            for (let index = 0; index < elements.length; index += 1) {
                const val = value[index];
                if (!(Array.isArray(val) ? val.length : val)) {
                    isEveryFieldFilled = false;
                    break;
                }
            }
            return !isEveryFieldFilled;
        }

        if (required === 'some') return !value.some((val) => (Array.isArray(val) ? val.length : val));

        if (Array.isArray(required)) {
            let disabled = false;

            required.forEach((id) => {
                const index = elements.findIndex((element) => element.id === id);
                if (index === -1) return;
                if (!value[index] || (Array.isArray(value[index]) && !(value[index] as Array<unknown>).length))
                    disabled = true;
            });

            return disabled;
        }

        return false;
    };

    const handleTextChange = (e: ChangeEvent<HTMLInputElement | HTMLTextAreaElement>, index: number) => {
        setValue((prev) => {
            const newValue = [...prev];
            newValue[index] = e.target.value;
            return newValue;
        });
    };

    const renderElement = useCallback(
        (element: InputFormConfig['elements'][0], index: number) => {
            const { config, id, type, label } = element;
            let textConfig: InputTextConfig | undefined;
            let textareaConfig: InputTextareaConfig | undefined;
            let fileConfig: InputFileConfig | undefined;

            switch (type) {
                case InputType.MULTISELECT:
                    return (
                        <CheckboxList
                            key={id}
                            data={(config as InputSelectConfig).optionList}
                            onChange={(data) => onChange(data, index)}
                        />
                    );

                case InputType.SELECT:
                    return (
                        <RadioList
                            key={id}
                            data={(config as InputSelectConfig).optionList}
                            onChange={(data) => onChange(data, index)}
                        />
                    );

                case InputType.TEXT:
                    textConfig = config as InputTextConfig;
                    return (
                        <Input
                            label={label}
                            key={id}
                            placeholder={textConfig.placeholder}
                            maxLength={textConfig.maxLength}
                            minLength={textConfig.minLength}
                            pattern={textConfig.pattern}
                            required={textConfig.required}
                            onChange={(e: ChangeEvent<HTMLInputElement>) => handleTextChange(e, index)}
                            value={(value[index] as string) || ''}
                            variant='outlined'
                            className='w-full'
                        />
                    );

                case InputType.TEXTAREA:
                    textareaConfig = config as InputTextareaConfig;
                    return (
                        <Textarea
                            key={id}
                            value={(value[index] as string) || ''}
                            placeholder={textareaConfig.placeholder}
                            maxLength={textareaConfig.maxLength}
                            rows={textareaConfig.rows}
                            onChange={(e) => handleTextChange(e, index)}
                        />
                    );

                case InputType.FILE:
                    fileConfig = config as InputFileConfig;
                    return (
                        <div key={id} className='flex flex-col gap-2'>
                            <FileUploader
                                label={fileConfig.label}
                                value={getFileUploaderValue(value[index], fileConfig.multiple)}
                                onChange={(files) => onChange(files, index)}
                                onError={() => setToastVisible(true)}
                                accept={fileConfig.accept}
                                multiple={fileConfig.multiple}
                                maxFileSize={fileConfig.maxSize ? fileConfig.maxSize / (1024 * 1024) : undefined}
                                helperText={fileConfig.helperText}
                                maxFiles={fileConfig.maxFiles}
                            />
                        </div>
                    );

                default:
                    return null;
            }
        },
        [value],
    );

    if (useDialog) {
        return (
            <>
                <div className='flex gap-10 mr-40 ml-20 flex-wrap'>
                    {buttonList ? (
                        buttonList.map((button) => (
                            <Button
                                key={button.name}
                                variant='outlined'
                                onClick={() => (button.type === 'toggle' ? setDialogOpen(true) : onButtonClick(button))}
                                className='!px-20 !py-[8px]'
                            >
                                {button.name}
                            </Button>
                        ))
                    ) : (
                        <Button variant='outlined' onClick={() => setDialogOpen(true)} className='!px-20 !py-[8px]'>
                            {dialogTitle || 'Open Form'}
                        </Button>
                    )}
                </div>
                <Dialog open={dialogOpen} onClose={() => setDialogOpen(false)} classes={{ container: '!min-w-full' }}>
                    <DialogHeader onClose={() => setDialogOpen(false)} title={dialogTitle || 'Form'} />
                    <div className='flex flex-col gap-15 grow p-20'>
                        {elements.map((element, index) => (
                            <div key={element.id} className='mb-10'>
                                <Typography className='mb-2' dangerouslySetInnerHTML={{ __html: element.label }} />
                                {renderElement(element, index)}
                            </div>
                        ))}
                        <Button size='lg' onClick={handleSubmit} className='w-full' disabled={getIsDisabled()}>
                            {review ? reviewBtnLabel || 'Review' : submitBtnLabel || 'Submit'}
                        </Button>
                    </div>
                </Dialog>
                <FormReview
                    elements={elements}
                    value={value}
                    open={reviewOpen}
                    onClose={() => setReviewOpen(false)}
                    onSubmit={submitForm}
                    loading={loading}
                    reviewDialogTitle={reviewDialogTitle}
                    reviewDialogDescription={reviewDialogDescription}
                    submitBtnLabel={submitBtnLabel}
                />
                <Toast
                    heading={TOAST_MESSAGE}
                    visible={toastVisible}
                    onBackdropClick={() => setToastVisible(false)}
                    iconLeft={<ExclamationCircle fontSize={20} />}
                />
            </>
        );
    }

    // Default: message bubble UI
    return (
        <>
            {elements.map((element, index) => (
                <MessageBubble key={element.id} actor={{ type: ActorType.BOT }} className='mr-40 ml-20'>
                    <Typography dangerouslySetInnerHTML={{ __html: element.label }} />
                    {renderElement(element, index)}
                </MessageBubble>
            ))}
            <div className='mr-40 ml-20'>
                <Button
                    className='!px-20 !py-[8px]'
                    variant='outlined'
                    onClick={handleSubmit}
                    disabled={getIsDisabled()}
                >
                    {submitBtnLabel || 'Submit'}
                </Button>
            </div>
            <FormReview
                elements={elements}
                value={value}
                open={reviewOpen}
                onClose={() => setReviewOpen(false)}
                onSubmit={submitForm}
                loading={loading}
            />
            <Toast
                heading={TOAST_MESSAGE}
                visible={toastVisible}
                onBackdropClick={() => setToastVisible(false)}
                iconLeft={<ExclamationCircle fontSize={20} />}
            />
        </>
    );
};

export default FormInput;
